import React from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UserConfigForm from './UserConfigForm';
import { tokens, withAlpha } from '../../Common/designTokens';
import { useUserConfigFormStore } from '../../Store/userConfigFormStore';
import { useAuth } from '../../Context/AuthProvider';
import { updateUserConfig } from '../../http/api';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: tokens.colors.bodyBackgroundColor,
  boxShadow: 24,
  borderRadius: '8px',
  outline: 'none',
  p: 0,
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  px: 2,
  py: 1,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '15px',
    right: '20px',
    borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
  },
};

const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  p: 2,
  borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
  gap: '12px',
};

const buttonStyle = {
  textTransform: 'none',
  borderRadius: '8px',
  height: '42px',
  paddingX: '24px',
  fontWeight: 600,
  fontSize: '14px',
};

const UserConfigPopup = ({
  open,
  onClose,
  mode = 'add',
  initialData = null,
  onSuccess,
  showSuccess,
  showError,
  canWrite = false,
}) => {
  const { token } = useAuth();
  const { formData, setFieldErrors, resetFormData, setLoading, loading, error, setError } = useUserConfigFormStore();
  const writeAccessDeniedMessage = 'Only SUPER_ADMIN can modify session and user configuration.';

  const handleClose = () => {
    setError(null);
    resetFormData();
    onClose();
  };

  const validateForm = () => {
    const errors = {};
    
    // Required field validation
    if (!formData.userId) errors.userId = true;
    if (!formData.venueId) errors.venueId = true;
    if (!formData.venueUser?.trim()) errors.venueUser = true;

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!canWrite) {
      setError(writeAccessDeniedMessage);
      if (showError) showError(writeAccessDeniedMessage);
      return;
    }

    if (!validateForm()) {
      console.log('Validation failed');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        venueId: parseInt(formData.venueId),
        userId: parseInt(formData.userId),
        venueUser: formData.venueUser,
        sessionUpdateAction: mode === 'add' ? 'ADD' : 'EDIT',
      };

      // Include mapping ID for update operations
      if (mode === 'edit' && formData.fixUserConfigMappingId) {
        payload.fixUserConfigMappingId = formData.fixUserConfigMappingId;
      }

      const response = await updateUserConfig(token, payload);

      if (response?.success) {
        const successMsg = response?.message || `User configuration ${mode === 'add' ? 'added' : 'updated'} successfully`;
        if (showSuccess) showSuccess(successMsg);
        handleClose();
        if (onSuccess) onSuccess(successMsg);
      } else {
        // Handle success: false case - show message from API
        const errorMsg = response?.message || `Failed to ${mode === 'add' ? 'add' : 'update'} user configuration`;
        setError(errorMsg);
        if (showError) showError(errorMsg);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      // Handle error case (400, 500, etc.)
      const errorMsg = err.message || `Failed to ${mode === 'add' ? 'add' : 'update'} user configuration`;
      setError(errorMsg);
      if (showError) showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    return mode === 'add' ? 'Add User Configuration' : 'Edit User Configuration';
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      disableEscapeKeyDown={true}
      aria-labelledby="user-config-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '95vh',
          width: '800px',
          maxWidth: '90vw',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={headerStyle}>
          <Typography
            id="user-config-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: 'bold', color: tokens.colors.fontColour }}
          >
            {getModalTitle()}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: tokens.colors.fontColour }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <UserConfigForm mode={mode} initialData={initialData} />
        </Box>

        {/* Footer */}
        <Box sx={{ ...footerStyle, flexDirection: 'column' }}>
          {error && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                border: `1px solid ${tokens.palette.danger}`,
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ color: tokens.palette.danger, fontSize: '14px', fontWeight: 500 }}>
                {error}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              ...buttonStyle,
              color: tokens.colors.fontColour,
              borderColor: tokens.colors.bodyBorderColor50,
              backgroundColor: tokens.colors.white,
              '&:hover': {
                backgroundColor: tokens.colors.defaultGridHoverColor,
                borderColor: tokens.palette.primary,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !canWrite}
            title={!canWrite ? writeAccessDeniedMessage : undefined}
            sx={{
              ...buttonStyle,
              backgroundColor: tokens.palette.primary,
              color: tokens.colors.white,
              '&:hover': {
                backgroundColor: tokens.palette.primaryDark,
              },
              '&:disabled': {
                backgroundColor: tokens.palette.primary,
                opacity: 0.6,
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: tokens.colors.white }} />
                {mode === 'add' ? 'Adding...' : 'Updating...'}
              </>
            ) : (
              'Submit'
            )}
          </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserConfigPopup;
